import { Controller, Control, FieldErrors } from "react-hook-form";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { useTranslation } from "react-i18next";
import { Hideable } from "shared/UI/Hideable/Hideable";
import { communicationSendOptions, communicationTypes } from "shared/constants/Modules";
import { ClientDivisionSelector } from "../../../UIPalette/components/ClientDivisionSelector/ClientDivisionSelector";
import { TargetGroupTypeAhead } from "../../../UIPalette/components/TargetGroupTypeAhead/TargetGroupTypeAhead";
import { LocaleSelect } from "shared/components/LocaleSelect";
import { PushNotifications } from "./PushNotifications";
import { Emails } from "./Emails";
import { Inputs } from "../Communications";

export const Fields = ({
  control,
  errors,
  communicationTarget,
  activeType,
}: {
  communicationTarget?: string;
  activeType?: string;
  control: Control<Inputs, any>;
  errors: FieldErrors<Inputs>;
}) => {
  const { t } = useTranslation();

  const getLocaleController = ({ validateRule, showAllOptions }: { validateRule?: boolean; showAllOptions?: boolean }) => {
    return (
      <Controller
        name={"localeId"}
        control={control}
        rules={validateRule ? { validate: (localeId) => !!localeId } : undefined}
        render={({ field }) => (
          <>
            <LocaleSelect {...{ ...field, ref: undefined }} allOption={showAllOptions} label={t("communications.localeSelectLabel")} ariaLabel={t("communications.localeSelectLabel")} />
            {errors.clientDivision && <ErrorAlert content={t("communications.localeSelectError")} />}
          </>
        )}
      />
    );
  };

  return (
    <div data-testid="communications-fields" className="mt-2 space-y-6">
      <h3>{t("communications.fieldsTitle")}</h3>
      <Hideable hidden={communicationTarget !== communicationSendOptions.SPECIFIC_CLIENT}>
        <Controller
          name={"clientDivision"}
          control={control}
          rules={{ validate: ({ client }) => !!client?.id }}
          render={({ field: { onChange } }) => (
            <>
              <ClientDivisionSelector onSelection={onChange} />
              {errors.clientDivision && <ErrorAlert content={t("communications.clientRequiredError")} />}
            </>
          )}
        />
      </Hideable>
      <Hideable hidden={communicationTarget !== communicationSendOptions.SPECIFIC_TARGET_GROUP}>
        <Controller
          name={"targetGroup"}
          control={control}
          rules={{ validate: (targetGroup) => !!targetGroup }}
          render={({ field: { onChange } }) => (
            <>
              <TargetGroupTypeAhead onTargetGroupSelected={onChange} />
              {errors.targetGroup && <ErrorAlert content={t("communications.targetGroupRequiredError")} />}
            </>
          )}
        />
      </Hideable>
      <Hideable hidden={communicationTarget !== communicationSendOptions.SPECIFIC_LOCATION}>{getLocaleController({ validateRule: true })}</Hideable>
      <Hideable hidden={communicationTarget !== communicationSendOptions.ONLY_PERKS_AND_DISCOUNT}>
        {getLocaleController({ showAllOptions: true })}
      </Hideable>
      <Hideable hidden={activeType !== communicationTypes.PUSH_NOTIFICATION}>
        <PushNotifications control={control} errors={errors} />
      </Hideable>
      <Hideable hidden={activeType !== communicationTypes.EMAIL}>
        <Emails control={control} errors={errors} />
      </Hideable>
    </div>
  );
};
